import { CircleAlert, Clock, LoaderCircle, X } from 'lucide-react';
import type { OpenTab } from '../../shared/api';
import { youTubeThumbnailUrl } from '../../shared/youtube-url';
import { Thumbnail } from './Thumbnail';

/** State of a tab's summary, if worth showing. */
export type TabStatus = 'running' | 'waiting' | 'error' | 'unread' | undefined;

export interface TabListProps {
  tabs: OpenTab[];
  /** The video shown right now. */
  currentId?: string | undefined;
  status: (videoId: string) => TabStatus;
  onOpen: (tab: OpenTab) => void;
  onClose: (tab: OpenTab) => void;
  /** Accessible name of the list. */
  label: string;
  className?: string;
}

export function tabTitle(tab: OpenTab): string {
  return tab.title || 'YouTube-Video';
}

const STATUS_TEXT: Record<Exclude<TabStatus, undefined>, string> = {
  running: 'Wird erstellt …',
  waiting: 'Wartet …',
  error: 'Fehlgeschlagen',
  unread: 'Neu',
};

function StatusLabel({ status }: { status: Exclude<TabStatus, undefined> }) {
  const icon =
    status === 'running' ? (
      <LoaderCircle size={13} className="spin" aria-hidden="true" />
    ) : status === 'waiting' ? (
      <Clock size={13} aria-hidden="true" />
    ) : status === 'error' ? (
      <CircleAlert size={13} aria-hidden="true" />
    ) : (
      <span className="tab-status__dot" aria-hidden="true" />
    );
  return (
    <span className={`tab-status tab-status--${status}`}>
      {icon}
      {STATUS_TEXT[status]}
    </span>
  );
}

/** Open tabs with thumbnail, title, channel / state, and a close button each. */
export function TabList({ tabs, currentId, status, onOpen, onClose, label, className }: TabListProps) {
  return (
    <ul className={`tab-list${className ? ` ${className}` : ''}`} aria-label={label}>
      {tabs.map((tab) => {
        const current = tab.videoId === currentId;
        const state = status(tab.videoId);
        const title = tabTitle(tab);
        return (
          <li key={tab.videoId} className={`tab-item${current ? ' is-current' : ''}`}>
            <button
              type="button"
              className="tab-item__open"
              onClick={() => onOpen(tab)}
              aria-current={current ? 'page' : undefined}
            >
              <Thumbnail className="tab-item__thumb" src={youTubeThumbnailUrl(tab.videoId, 'mqdefault')} width={80} height={45} />
              <span className="tab-item__text">
                <span className="tab-item__title">{title}</span>
                <span className="tab-item__meta">
                  {state && <StatusLabel status={state} />}
                  {tab.channel && <span className="tab-item__channel">{tab.channel}</span>}
                  {current && <span className="sr-only">(wird gerade angezeigt)</span>}
                </span>
              </span>
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--subtle tab-item__close"
              onClick={() => onClose(tab)}
              aria-label={`„${title}“ schließen`}
              title="Tab schließen"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
